import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft,
  ShieldCheck,
  Loader2,
  Save,
  Settings,
  Sparkles,
  Plus,
  X,
  Download,
  FileCheck2,
  BookMarked,
} from "lucide-react";
import RichTextEditor from "@/components/app/RichTextEditor";
import AIChatPanel from "@/components/app/AIChatPanel";
import CoherenceModal from "@/components/app/CoherenceModal";
import CoherencePanel from "@/components/app/CoherencePanel";
import RagPanel from "@/components/app/RagPanel";

const STATUS_OPTIONS = [
  { value: "draft", label: "Borrador" },
  { value: "in_progress", label: "En curso" },
  { value: "review", label: "En revisión" },
  { value: "ready_for_defense", label: "Lista para defensa" },
];

export default function ProjectEditor() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingMeta, setSavingMeta] = useState(false);
  const [autoSaveState, setAutoSaveState] = useState("idle"); // idle | saving | saved | error
  const [rightTab, setRightTab] = useState("chat"); // chat | context | coherence
  const [validating, setValidating] = useState(false);
  const [coherenceReport, setCoherenceReport] = useState(null);
  const [coherenceError, setCoherenceError] = useState(null);

  // Local editable state
  const [content, setContent] = useState({ html: "", text: "" });
  const [problem, setProblem] = useState("");
  const [objectives, setObjectives] = useState([]);
  const [methodology, setMethodology] = useState("");
  const [newObjective, setNewObjective] = useState("");
  const [meta, setMeta] = useState({ title: "", career: "", university: "", status: "draft", progress_percent: 0 });

  const saveTimer = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}`);
      setProject(data);
      setContent({ html: data.content_html || "", text: data.content_text || "" });
      setProblem(data.research_problem || "");
      setObjectives(data.objectives || []);
      setMethodology(data.methodology || "");
      setMeta({
        title: data.title,
        career: data.career,
        university: data.university || "",
        status: data.status,
        progress_percent: data.progress_percent,
      });
    } catch (e) {
      toast.error("No se pudo cargar el proyecto");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [projectId]);

  // Debounced autosave of content
  const onEditorChange = (next) => {
    setContent(next);
    setAutoSaveState("saving");
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await api.patch(`/projects/${projectId}`, {
          content_html: next.html,
          content_text: next.text,
        });
        setAutoSaveState("saved");
        setTimeout(() => setAutoSaveState("idle"), 1500);
      } catch {
        setAutoSaveState("error");
      }
    }, 900);
  };

  const saveContext = async () => {
    setSavingMeta(true);
    try {
      const { data } = await api.patch(`/projects/${projectId}`, {
        title: meta.title,
        career: meta.career,
        university: meta.university || null,
        status: meta.status,
        progress_percent: Number(meta.progress_percent),
        research_problem: problem,
        objectives: objectives,
        methodology: methodology,
      });
      setProject(data);
      toast.success("Contexto guardado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar el contexto");
    } finally {
      setSavingMeta(false);
    }
  };

  const addObjective = () => {
    const v = newObjective.trim();
    if (!v) return;
    setObjectives((o) => [...o, v]);
    setNewObjective("");
  };

  const removeObjective = (i) => {
    setObjectives((o) => o.filter((_, idx) => idx !== i));
  };

  const onValidateCoherence = async () => {
    if (validating) return;
    if (!problem.trim() || objectives.length === 0) {
      toast.error("Define problema y al menos 1 objetivo en la pestaña Contexto.");
      setRightTab("context");
      return;
    }
    if ((content.text || "").trim().length < 80) {
      toast.error("Escribe al menos 80 caracteres en el editor.");
      return;
    }
    setValidating(true);
    setCoherenceError(null);
    setRightTab("coherence");
    try {
      // Ensure latest is saved first
      if (saveTimer.current) {
        clearTimeout(saveTimer.current);
        await api.patch(`/projects/${projectId}`, {
          content_html: content.html,
          content_text: content.text,
          research_problem: problem,
          objectives,
          methodology,
        });
      }
      const { data } = await api.post(`/projects/${projectId}/validate-coherence`);
      setCoherenceReport(data.report);
      toast.success("Análisis listo");
    } catch (e) {
      const detail = e?.response?.data?.detail || "No se pudo validar la coherencia";
      setCoherenceError(detail);
      toast.error(detail);
    } finally {
      setValidating(false);
    }
  };

  const contextReady = problem.trim().length > 0 && objectives.length > 0;

  /* ----- Export to .docx with institutional formatting (Prompt 10) ----- */
  const [exporting, setExporting] = useState(false);

  const onExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      // Ensure latest content is saved before exporting
      if (saveTimer.current) {
        clearTimeout(saveTimer.current);
        await api.patch(`/projects/${projectId}`, {
          content_html: content.html,
          content_text: content.text,
        });
      }
      const response = await api.post(
        `/projects/${projectId}/export`,
        {},
        { responseType: "blob" }
      );
      const blob = new Blob([response.data], {
        type:
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });
      const headerName = response.headers?.["x-filename"];
      const filename =
        headerName ||
        `${(meta.title || project.title || "tesis").replace(/[^\w\-\sáéíóúñÁÉÍÓÚÑ]/g, "").replace(/\s+/g, "_") || "tesis"}.docx`;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Documento exportado");
    } catch (e) {
      let detail = "No se pudo exportar el documento";
      if (e?.response?.data instanceof Blob) {
        try {
          const txt = await e.response.data.text();
          detail = JSON.parse(txt)?.detail || detail;
        } catch {
          // ignore
        }
      } else if (e?.response?.data?.detail) {
        detail = e.response.data.detail;
      }
      toast.error(detail);
    } finally {
      setExporting(false);
    }
  };

  if (loading || !project) {
    return (
      <div className="p-10 text-ink-soft">Cargando proyecto…</div>
    );
  }

  return (
    <div className="h-[calc(100vh-3.5rem)] lg:h-screen flex flex-col bg-paper" data-testid="project-editor">
      {/* Top bar */}
      <div className="flex items-center gap-3 px-4 lg:px-6 h-14 border-b border-rule bg-paper/85 backdrop-blur-xl shrink-0">
        <Link
          to="/dashboard"
          data-testid="back-to-projects"
          className="inline-flex items-center gap-1.5 text-[12.5px] text-ink-soft hover:text-forest"
        >
          <ArrowLeft size={14} />
          <span className="hidden sm:inline">Mis proyectos</span>
        </Link>
        <span className="h-5 w-px bg-rule" />
        <div className="min-w-0 flex-1">
          <p className="text-[10px] tracking-[0.22em] uppercase text-copper font-medium">
            {project.citation_format}
          </p>
          <h1 className="font-serif text-[16px] text-ink leading-tight truncate" data-testid="editor-project-title">
            {meta.title || project.title}
          </h1>
        </div>
        <AutosaveBadge state={autoSaveState} />
        <button
          data-testid="export-doc-btn"
          onClick={onExport}
          disabled={exporting}
          className="group inline-flex items-center gap-2 px-4 py-2 rounded-full text-[12.5px] font-medium text-forest bg-white border border-forest/30 hover:bg-forest hover:text-paper disabled:opacity-60 transition-all shadow-sm hover:shadow-md hover:-translate-y-[1px]"
        >
          {exporting ? (
            <Loader2 size={13} className="animate-spin" />
          ) : (
            <Download size={13} />
          )}
          <span className="hidden sm:inline">{exporting ? "Exportando…" : "Exportar documento"}</span>
        </button>
        <button
          data-testid="validate-coherence-btn"
          onClick={onValidateCoherence}
          disabled={validating}
          className="group inline-flex items-center gap-2 px-4 py-2 rounded-full text-[12.5px] font-medium text-paper bg-forest hover:bg-forest-dark disabled:opacity-60 transition-all shadow-sm hover:shadow-md hover:-translate-y-[1px]"
        >
          {validating ? (
            <Loader2 size={13} className="animate-spin" />
          ) : (
            <ShieldCheck size={13} />
          )}
          {validating ? "Validando…" : "Validar coherencia"}
        </button>
      </div>

      {/* Split body */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[1fr_400px] min-h-0">
        {/* Editor */}
        <div className="flex flex-col min-h-0 border-r border-rule">
          <RichTextEditor
            initialHtml={project.content_html}
            onChange={onEditorChange}
            placeholder="Comienza tu capítulo aquí… Puedes pegar tu borrador o escribir desde cero. El copiloto leerá tu texto."
            templateStyle={project.applied_template_snapshot}
          />
        </div>

        {/* Right panel: tabs */}
        <div className="flex flex-col min-h-0 max-h-[60vh] lg:max-h-none">
          <div className="flex items-center gap-1 px-3 py-2 border-b border-rule bg-white">
            <TabBtn
              testid="tab-chat"
              active={rightTab === "chat"}
              onClick={() => setRightTab("chat")}
              icon={Sparkles}
              label="Copiloto"
            />
            <TabBtn
              testid="tab-coherence"
              active={rightTab === "coherence"}
              onClick={() => setRightTab("coherence")}
              icon={ShieldCheck}
              label="Coherencia"
              badge={coherenceReport ? `${coherenceReport.metodologia_score ?? coherenceReport.alignment_score}` : null}
            />
            <TabBtn
              testid="tab-research"
              active={rightTab === "research"}
              onClick={() => setRightTab("research")}
              icon={BookMarked}
              label="Investigar"
              badge="RAG"
            />
            <TabBtn
              testid="tab-context"
              active={rightTab === "context"}
              onClick={() => setRightTab("context")}
              icon={Settings}
              label="Contexto"
              badge={!contextReady ? "Definir" : null}
            />
          </div>
          <div className="flex-1 min-h-0">
            {rightTab === "chat" ? (
              <AIChatPanel projectId={projectId} disabled={false} />
            ) : rightTab === "coherence" ? (
              <CoherencePanel
                report={coherenceReport}
                loading={validating}
                error={coherenceError}
                onGoToContext={() => setRightTab("context")}
                onRevalidate={onValidateCoherence}
              />
            ) : rightTab === "research" ? (
              <RagPanel
                projectId={projectId}
                defaultQuery={meta.title || project.title || ""}
                onInsertIntoEditor={(html) => {
                  const next = `${content.html || ""}${html}`;
                  // emulate editor change so autosave kicks in
                  onEditorChange({ html: next, text: (content.text || "") + "\n" + (html.replace(/<[^>]+>/g, "")) });
                  toast.info("Recarga el proyecto si no ves el bloque insertado");
                }}
              />
            ) : (
              <ContextPanel
                meta={meta}
                setMeta={setMeta}
                problem={problem}
                setProblem={setProblem}
                objectives={objectives}
                setObjectives={setObjectives}
                methodology={methodology}
                setMethodology={setMethodology}
                newObjective={newObjective}
                setNewObjective={setNewObjective}
                addObjective={addObjective}
                removeObjective={removeObjective}
                saveContext={saveContext}
                savingMeta={savingMeta}
              />
            )}
          </div>
        </div>
      </div>

      {/* Legacy modal — no longer auto-opened, available via state if needed */}

      {exporting && <ExportingOverlay />}
    </div>
  );
}

function ExportingOverlay() {
  return (
    <div
      data-testid="export-loading-overlay"
      className="fixed inset-0 z-[55] flex items-center justify-center px-4"
    >
      <div className="absolute inset-0 bg-ink/40 backdrop-blur-md" />
      <div className="relative w-full max-w-sm rounded-2xl bg-paper border border-rule shadow-[0_30px_80px_-20px_rgba(39,59,44,0.45)] p-7 text-center animate-fade-up">
        <div className="mx-auto h-14 w-14 grid place-items-center rounded-full bg-forest text-paper relative">
          <Loader2 size={20} className="animate-spin" />
          <span className="absolute -bottom-1 -right-1 h-6 w-6 grid place-items-center rounded-full bg-copper text-paper border-2 border-paper">
            <FileCheck2 size={11} />
          </span>
        </div>
        <p className="mt-4 text-[10px] tracking-[0.22em] uppercase text-copper font-medium">
          Exportar documento
        </p>
        <p
          data-testid="export-loading-text"
          className="mt-2 font-serif text-lg text-ink leading-snug text-balance"
        >
          Aplicando formato institucional y verificando consistencia de citas…
        </p>
        <div className="mt-5 flex items-center justify-center gap-1.5">
          <span className="h-1.5 w-10 rounded-full bg-forest animate-pulse" />
          <span className="h-1.5 w-10 rounded-full bg-copper animate-pulse [animation-delay:.15s]" />
          <span className="h-1.5 w-10 rounded-full bg-rule animate-pulse [animation-delay:.3s]" />
        </div>
      </div>
    </div>
  );
}

function AutosaveBadge({ state }) {
  if (state === "saving")
    return (
      <span className="hidden sm:inline-flex items-center gap-1.5 text-[11px] text-ink-soft mr-1">
        <Loader2 size={11} className="animate-spin" />
        Guardando…
      </span>
    );
  if (state === "saved")
    return (
      <span className="hidden sm:inline-flex items-center gap-1.5 text-[11px] text-forest mr-1">
        <Save size={11} />
        Guardado
      </span>
    );
  if (state === "error")
    return (
      <span className="hidden sm:inline-flex items-center gap-1.5 text-[11px] text-[#B6533F] mr-1">
        ⚠ Error guardando
      </span>
    );
  return null;
}

function TabBtn({ active, onClick, icon: Icon, label, testid, badge }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12.5px] transition-colors ${
        active
          ? "bg-forest text-paper"
          : "text-ink-soft hover:bg-paper-2 hover:text-ink"
      }`}
    >
      <Icon size={12} />
      {label}
      {badge && (
        <span className="ml-1 text-[9px] tracking-[0.18em] uppercase font-medium text-copper bg-[#FBF6EC] rounded-full px-1.5">
          {badge}
        </span>
      )}
    </button>
  );
}

function ContextPanel({
  meta,
  setMeta,
  problem,
  setProblem,
  objectives,
  methodology,
  setMethodology,
  newObjective,
  setNewObjective,
  addObjective,
  removeObjective,
  saveContext,
  savingMeta,
}) {
  return (
    <div className="h-full overflow-y-auto p-5 flex flex-col gap-5 bg-paper-2/30" data-testid="context-panel">
      {/* Problem */}
      <Section label="Problema de investigación">
        <textarea
          data-testid="ctx-problem"
          value={problem}
          onChange={(e) => setProblem(e.target.value)}
          rows={4}
          maxLength={2000}
          placeholder="Ej. ¿En qué medida la adopción de IA generativa influye en el desempeño escolar de estudiantes de secundaria en el Edo. de México?"
          className="w-full px-3 py-2.5 rounded-lg border border-rule bg-white text-[13px] outline-none focus:border-forest focus:ring-2 focus:ring-forest/10 resize-none"
        />
      </Section>

      {/* Objectives */}
      <Section label="Objetivos del estudio">
        <ul className="flex flex-col gap-2 mb-2" data-testid="ctx-objectives">
          {objectives.map((o, i) => (
            <li
              key={i}
              data-testid={`ctx-objective-${i}`}
              className="flex items-start gap-2 rounded-lg border border-rule bg-white px-3 py-2"
            >
              <span className="grid place-items-center h-5 w-5 rounded-full bg-forest text-paper text-[10px] mt-0.5 shrink-0">
                {i + 1}
              </span>
              <p className="flex-1 text-[12.5px] text-ink leading-snug">{o}</p>
              <button
                aria-label="Quitar"
                onClick={() => removeObjective(i)}
                className="text-ink-soft hover:text-[#B6533F]"
              >
                <X size={13} />
              </button>
            </li>
          ))}
          {objectives.length === 0 && (
            <li className="text-[12px] text-ink-soft italic">
              Agrega al menos un objetivo (general o específico).
            </li>
          )}
        </ul>
        <div className="flex gap-2">
          <input
            data-testid="ctx-new-objective"
            value={newObjective}
            onChange={(e) => setNewObjective(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addObjective();
              }
            }}
            placeholder="Ej. Determinar el impacto de…"
            className="flex-1 px-3 py-2 rounded-lg border border-rule bg-white text-[12.5px] outline-none focus:border-forest focus:ring-2 focus:ring-forest/10"
          />
          <button
            data-testid="ctx-add-objective"
            onClick={addObjective}
            className="grid place-items-center h-9 w-9 rounded-full bg-forest text-paper hover:bg-forest-dark"
            aria-label="Agregar"
          >
            <Plus size={14} />
          </button>
        </div>
      </Section>

      {/* Methodology (Prompt 6) */}
      <Section label="Metodología">
        <textarea
          data-testid="ctx-methodology"
          value={methodology}
          onChange={(e) => setMethodology(e.target.value)}
          rows={4}
          maxLength={4000}
          placeholder="Ej. Estudio cualitativo descriptivo, 3 estudios de caso en secundarias del Edo. de México, entrevistas semiestructuradas + rúbrica Paul-Elder pre/post."
          className="w-full px-3 py-2.5 rounded-lg border border-rule bg-white text-[13px] outline-none focus:border-forest focus:ring-2 focus:ring-forest/10 resize-none"
        />
        <p className="text-[10.5px] text-ink-soft/80 leading-snug">
          El metodólogo IA usará este campo para validar la coherencia entre pregunta, objetivos y método.
        </p>
      </Section>

      {/* Metadata */}
      <Section label="Datos del proyecto">
        <Field label="Título">
          <input
            data-testid="ctx-title"
            value={meta.title}
            onChange={(e) => setMeta({ ...meta, title: e.target.value })}
            className="px-3 py-2 rounded-lg border border-rule bg-white text-[12.5px] outline-none focus:border-forest"
          />
        </Field>
        <Field label="Carrera">
          <input
            data-testid="ctx-career"
            value={meta.career}
            onChange={(e) => setMeta({ ...meta, career: e.target.value })}
            className="px-3 py-2 rounded-lg border border-rule bg-white text-[12.5px] outline-none focus:border-forest"
          />
        </Field>
        <Field label="Universidad">
          <input
            data-testid="ctx-university"
            value={meta.university}
            onChange={(e) => setMeta({ ...meta, university: e.target.value })}
            placeholder="Opcional"
            className="px-3 py-2 rounded-lg border border-rule bg-white text-[12.5px] outline-none focus:border-forest"
          />
        </Field>
        <Field label={`Progreso · ${meta.progress_percent || 0}%`}>
          <input
            data-testid="ctx-progress"
            type="range"
            min={0}
            max={100}
            value={meta.progress_percent || 0}
            onChange={(e) => setMeta({ ...meta, progress_percent: e.target.value })}
            className="accent-forest"
          />
        </Field>
        <Field label="Estado">
          <select
            data-testid="ctx-status"
            value={meta.status}
            onChange={(e) => setMeta({ ...meta, status: e.target.value })}
            className="px-3 py-2 rounded-lg border border-rule bg-white text-[12.5px] outline-none focus:border-forest"
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </Field>
      </Section>

      <button
        data-testid="ctx-save-btn"
        onClick={saveContext}
        disabled={savingMeta}
        className="mt-1 inline-flex items-center justify-center gap-2 py-3 rounded-full text-[13px] font-medium text-paper bg-forest hover:bg-forest-dark disabled:opacity-60"
      >
        <Save size={13} />
        {savingMeta ? "Guardando…" : "Guardar contexto"}
      </button>
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div className="rounded-xl border border-rule bg-white/80 p-4">
      <p className="text-[10px] tracking-[0.22em] uppercase text-copper font-medium mb-2.5">
        {label}
      </p>
      <div className="flex flex-col gap-3">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] tracking-[0.22em] uppercase text-ink-soft/80 font-medium">
        {label}
      </span>
      {children}
    </label>
  );
}
